/* eslint-disable max-statements */
import { useLocation } from 'react-router-dom';
import assert from '../../core/util/assert';
import parseNumberFromQueryParam from '../../core/util/parseNumberFromQueryParam';
import type { FilterList } from '../queries/types';
import { ProductState } from '../queries/types';
import useStockLocations from '../../availability/hooks/useStockLocations';
import type { StockLocation } from '../../core/types/stores';
import useTireAttributes from '../components/TireSearchNew/hooks/useTireAttributes';

const NON_FILTER_LIST_KEYS = [
    'search',
    'minPrice',
    'maxPrice',
    'onSale',
    'states',
    'q',
    'vehicleId',
    'bicycleId',
    'pageSize',
    'sortField',
    'sortOrder',
    'viewMode',
    'sparePartParentId',
    'cartId',
    'bestAvailability',
    /front_.*/gm,
    /back_.*/gm,
];

const useStatesSearchParamFilters = () => {
    const { search, pathname } = useLocation();
    const parameters = new URLSearchParams(search);

    const onSale = pathname.includes('/sale');

    const states: ProductState[] = parameters.get('states')
        ? (parameters.getAll('states') as ProductState[])
        : [];

    if (onSale && !states.includes(ProductState.ON_SALE)) {
        states.push(ProductState.ON_SALE);
    }

    return states;
};

const usePriceSearchParamFilters = () => {
    const { search } = useLocation();
    const parameters = new URLSearchParams(search);

    const minPriceParam = parameters.get('minPrice');
    const minPrice = minPriceParam ? +minPriceParam : undefined;
    assert(!Number.isNaN(minPrice), 'minPrice cannot be NaN');

    const maxPriceParam = parameters.get('maxPrice');
    const maxPrice = maxPriceParam ? +maxPriceParam : undefined;
    assert(!Number.isNaN(maxPrice), 'maxPrice cannot be NaN');

    return [minPrice, maxPrice];
};

const useBestAvailabilitySearchParamFilter = () => {
    const { search } = useLocation();
    const parameters = new URLSearchParams(search);
    const stockLocations = useStockLocations().branches.map(
        item => item.id as StockLocation
    );

    if (!APP_ENV.FEATURE_AVAILABILITY_FILTER) {
        return undefined;
    }

    const bestAvailabilityValue = parameters.get('bestAvailability');
    return bestAvailabilityValue
        ? {
              stockLocations,
              value: +bestAvailabilityValue,
          }
        : undefined;
};

const useSearchParamFilters = () => {
    const { search } = useLocation();
    const parameters = new URLSearchParams(search);
    const states = useStatesSearchParamFilters();
    const [minPrice, maxPrice] = usePriceSearchParamFilters();
    const bestAvailability = useBestAvailabilitySearchParamFilter();

    const searchText = parameters.get('q') || undefined;

    const vehicleId = parameters.get('vehicleId') || undefined;
    assert(vehicleId !== '', 'vehicleId cannot be empty');

    const sparePartParentId = parameters.get('sparePartParentId') || undefined;
    assert(sparePartParentId !== '', 'sparePartParentId cannot be empty');

    const filters: FilterList[] = Array.from(new Set(parameters.keys()))
        .filter(
            name =>
                !NON_FILTER_LIST_KEYS.find(pattern => {
                    return pattern instanceof RegExp
                        ? name.match(pattern)
                        : pattern === name;
                })
        )
        .map(name => ({
            name,
            values: parameters.getAll(name),
        }));

    const { attributeFilters, frontAttributes, backAttributes } =
        useTireAttributes();

    return {
        minPrice,
        maxPrice,
        vehicleId,
        bicycleId: sparePartParentId,
        states,
        search: searchText,
        pageSize: parseNumberFromQueryParam(parameters.get('pageSize')),
        sortField: parameters.get('sortField'),
        sortOrder: parameters.get('sortOrder'),
        filters,
        bestAvailability,
        attributeFilters,
        frontAttributes,
        backAttributes,
    };
};

export default useSearchParamFilters;
